import { useTranslation } from "react-i18next";

import { Typography } from "@aviary";
import { useFlippers } from "@shared/hooks";
import { useLocation, useSearchParams } from "@shared/react-router-dom";
import {
  PatientStoreSignUpForm,
  SignUpModalHeader,
  SignUpModalTitle,
  StoreLandingLayout,
  StoreLogo,
  StoreOffersPill,
} from "@unauthenticated/signupSignin/components";
import type { PatientAttributes } from "@unauthenticated/signupSignin/data/types";
import { l } from "@unauthenticated/signupSignin/locales/i18n";

import * as styles from "./PatientStoreSignUpPage.styles";

interface HistoryState {
  source?: "RX_SIGN_IN";
  email?: string;
}

interface Props {
  patientAttributes: PatientAttributes;
  additionalAttributes?: any;
  pageVersion?: string;
  storeId?: string;
  isPreview?: boolean;
  isStoreOwner: boolean;
  ssoErrors?: string;
}

const PatientStoreSignUpPage = ({
  patientAttributes,
  additionalAttributes,
  pageVersion,
  isPreview = false,
  isStoreOwner,
  storeId,
  ssoErrors,
}: Props) => {
  const { t } = useTranslation();
  const { storeLogo, storeName, pracName, pracAvatar } = patientAttributes;
  const [isSwiftFillEnabled, isSupplementIntake] = useFlippers(
    "titan_swift_fill",
    "dynamo_supplement_plan"
  );
  const { state }: { state: HistoryState } = useLocation();
  const [searchParameters] = useSearchParams();

  const isIntake = searchParameters.get("intake") === "true";

  const renderIntakeSubtitle = () => {
    if (!isSupplementIntake || !isIntake) {
      return null;
    }

    return <Typography css={styles.centered}>{t(l.intake.signUpDescription)}</Typography>;
  };
  const isSwiftFillSignIn = state?.source === "RX_SIGN_IN";

  return (
    <StoreLandingLayout
      pracName={pracName}
      pracAvatar={pracAvatar}
      isPreview={isPreview}
      isStoreOwner={isStoreOwner}
    >
      <SignUpModalHeader>
        <StoreLogo storeLogo={storeLogo} storeName={storeName} />
        <SignUpModalTitle title={storeName} />
        {!isSwiftFillEnabled && !isSwiftFillSignIn && (
          <StoreOffersPill patientAttributes={patientAttributes} />
        )}
        {renderIntakeSubtitle()}
        {isSwiftFillEnabled && isSwiftFillSignIn && (
          <>
            <Typography type="h4">{t(l.auth.createYourAccount)}</Typography>
            <Typography css={styles.centered}>{t(l.auth.swiftfillSignupInstructions)}</Typography>
          </>
        )}
      </SignUpModalHeader>
      <PatientStoreSignUpForm
        storeId={storeId}
        pageVersion={pageVersion}
        patientAttributes={patientAttributes}
        additionalAttributes={additionalAttributes}
        isPreview={isPreview}
        ssoErrors={ssoErrors}
        preFilledEmail={state?.email}
        isSwiftFillSignIn={isSwiftFillSignIn}
        isIntake={isIntake}
      />
    </StoreLandingLayout>
  );
};

export { PatientStoreSignUpPage };
